import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";

type IndustryKey =
  | "dataScience"
  | "businessManagement"
  | "liberalArt"
  | "computerScience"
  | "socialSciences"
  | "healthcare"
  | "other";

export const CAREER_CARDS_VARIANTS = {
  dataScience: {
    heading: "Plan for a career in data science",
    cards: [
      {
        label: "Average 2024 salary",
        value: "$112,590",
        icon: "money-outline",
      },
      {
        label: "Estimated job openings",
        value: "23,400",
        icon: "people",
      },
      {
        label: "Estimated career growth",
        value: "34%",
        icon: "data",
      },
    ],
    skills: [
      "SQL",
      "Python",
      "Tableau",
      "Excel",
      "Statistics",
      "Data Visualization",
    ],
  },
  businessManagement: {
    heading: "Plan for a career in business and management",
    cards: [
      {
        label: "Average 2024 salary",
        value: "$80,920",
        icon: "money-outline",
      },
      {
        label: "Estimated job openings",
        value: "942,500",
        icon: "briefcase",
      },
      {
        label: "edX courses taken",
        value: "22M+",
        icon: "people",
      },
    ],
    skills: [
      "SQL",
      "Python",
      "Tableau",
      "Excel",
      "Statistics",
      "Data Visualization",
    ],
  },
  liberalArt: {
    heading: "Plan for a career in liberal art and humanities",
    cards: [
      {
        label: "Average 2025 salary",
        value: "$60,000",
        icon: "money-outline",
      },
      {
        label: "Job positions filled in 2025",
        value: "790,310",
        icon: "briefcase",
      },
      {
        label: "edX courses taken",
        value: "10M+",
        icon: "people",
      },
    ],
    skills: [
      "SQL",
      "Python",
      "Tableau",
      "Excel",
      "Statistics",
      "Data Visualization",
    ],
  },
  computerScience: {
    heading: "Plan for a career in computer science",
    cards: [
      {
        label: "Average 2024 salary",
        value: "$140,910",
        icon: "money-outline",
      },
      {
        label: "Estimated job openings",
        value: "3,200",
        icon: "people",
      },
      {
        label: "Estimated job growth",
        value: "20%",
        icon: "data",
      },
    ],
    skills: [
      "SQL",
      "Python",
      "Tableau",
      "Excel",
      "Statistics",
      "Data Visualization",
    ],
  },
  socialSciences: {
    heading: "Plan for a career in social sciences",
    cards: [
      {
        label: "Average 2025 salary",
        value: "$75,000",
        icon: "money-outline",
      },
      {
        label: "Estimated job filled",
        value: "5,226,990",
        icon: "briefcase",
      },
      {
        label: "edX courses taken",
        value: "6M+",
        icon: "people",
      },
    ],
    skills: [
      "SQL",
      "Python",
      "Tableau",
      "Excel",
      "Statistics",
      "Data Visualization",
    ],
  },
  healthcare: {
    heading: "Plan for a career in healthcare",
    cards: [
      {
        label: "Average 2024 salary",
        value: "$77,600",
        icon: "money-outline",
      },
      {
        label: "Estimated job openings",
        value: "1,945,200",
        icon: "people",
      },
      {
        label: "Estimated job growth",
        value: "13%",
        icon: "data",
      },
    ],
    skills: [
      "Patient Care",
      "Clinical Skills",
      "Medical Terminology",
      "Healthcare Management",
      "Anatomy",
      "Physiology",
    ],
  },
  other: {
    heading: "Plan for your future",
    cards: [
      {
        label: "Learners are upskilling  due to the economy",
        value: "65%",
        icon: "money-outline",
      },
      {
        label: "Learners agree upskilling is important to for job security",
        value: "70%",
        icon: "briefcase",
      },
      {
        label: "Learners report career growth with a certificate",
        value: "84%",
        icon: "people",
      },
    ],
    skills: [],
  },
} as const;

export const CAREER_CARDS_GOVERNMENT_DATA = {
  dataScience: "https://www.bls.gov/ooh/math/data-scientists.htm",
  businessManagement: "https://www.bls.gov/ooh/business-and-financial/",
  liberalArt:
    "https://www.bls.gov/ooh/field-of-degree/liberal-arts/liberal-arts-field-of-degree.htm",
  computerScience:
    "https://www.bls.gov/ooh/computer-and-information-technology/computer-and-information-research-scientists.htm",
  socialSciences:
    "https://www.bls.gov/ooh/field-of-degree/social-science/social-science-field-of-degree.htm",
  healthcare: "https://www.bls.gov/ooh/healthcare/home.htm",
};

export const ATTRIBUTE_TO_CATEGORY_MAP: Record<string, IndustryKey> = {
  "Data Analysis & Statistics": "dataScience",
  "Business & Management": "businessManagement",
  Architecture: "liberalArt",
  "Biology & Life Sciences": "healthcare",
  Communication: "liberalArt",
  Design: "liberalArt",
  "Education & Teacher Training": "liberalArt",
  "Food & Nutrition": "healthcare",
  "Health & Safety": "healthcare",
  Humanities: "liberalArt",
  Language: "liberalArt",
  Law: "liberalArt",
  Math: "liberalArt",
  Medicine: "healthcare",
  "Computer Science": "computerScience",
  "Social Sciences": "socialSciences",
  "Economics & Finance": "other",
  Engineering: "other",
  "Environmental Studies": "other",
};

export const getCategoryFromAttribute = (
  attribute: string,
):
  | "dataScience"
  | "businessManagement"
  | "liberalArt"
  | "computerScience"
  | "socialSciences"
  | "healthcare"
  | "other" => {
  return ATTRIBUTE_TO_CATEGORY_MAP[attribute] || "other";
};

const INDUSTRY_KEYWORDS: Record<IndustryKey, string[]> = {
  dataScience: [
    "data science",
    "ciencia de datos",
    "machine learning",
    "aprendizaje automático",
    "ml",
    "deep learning",
    "inteligencia artificial",
    "artificial intelligence",
    "ai",
    "analytics",
    "análisis de datos",
    "big data",
    "estadística",
    "statistics",
  ],

  businessManagement: [
    "business",
    "negocios",
    "management",
    "administración",
    "business & management",
    "negocios y administración",
    "mba",
    "marketing",
    "finanzas",
    "finance",
    "project management",
    "gestión de proyectos",
    "project manager",
    "operations",
    "operaciones",
    "supply chain",
  ],

  liberalArt: [
    "liberal arts",
    "artes liberales",
    "humanities",
    "humanidades",
    "philosophy",
    "filosofía",
    "history",
    "historia",
    "literature",
    "literatura",
    "art history",
    "historia del arte",
    "classics",
    "linguistics",
    "escritura creativa",
    "creative writing",
    "rhetoric",
    "retórica",
    "persuasive writing",
    "escritura persuasiva",
    "public speaking",
    "oratoria",
    "speech",
    "discurso",
    "communication",
    "comunicación",
    "writing",
    "escritura",
    "english",
    "inglés",
    "journalism",
    "periodismo",
    "media studies",
    "estudios de medios",
  ],

  computerScience: [
    "computer science",
    "informática",
    "informatics",
    "software",
    "programming",
    "programación",
    "cs",
    "algorithms",
    "algoritmos",
    "data structures",
    "estructuras de datos",
    "operating systems",
    "sistemas operativos",
  ],

  socialSciences: [
    "social science",
    "social sciences",
    "ciencias sociales",
    "sociology",
    "sociología",
    "psychology",
    "psicología",
    "political science",
    "ciencia política",
    "economics",
    "economía",
    "anthropology",
    "antropología",
    "international relations",
    "relaciones internacionales",
  ],

  healthcare: [
    "healthcare",
    "health care",
    "salud",
    "medicine",
    "medicina",
    "medical",
    "médico",
    "anatomy",
    "anatomía",
    "physiology",
    "fisiología",
    "nursing",
    "enfermería",
    "clinical",
    "clínico",
    "patient care",
    "hospital",
    "pharmacy",
    "farmacia",
    "public health",
    "salud pública",
    "epidemiology",
    "epidemiología",
    "biology",
    "biología",
    "life sciences",
    "ciencias de la vida",
    "nutrition",
    "nutrición",
    "health & safety",
    "dental",
    "cardiology",
    "neurology",
    "oncology",
    "pediatrics",
    "radiology",
    "surgery",
    "cirugía",
    "therapeutic",
    "diagnosis",
    "diagnóstico",
  ],

  other: [],
};

export function getIndustryKey({
  title,
  subjectEn,
  subjectEs,
  overview,
  subjects,
  skills,
}: {
  title: string;
  subjectEn: string;
  subjectEs: string;
  overview: string;
  subjects: Program["subjects"] | ProcessedCourse["subjects"];
  skills: Program["skills"] | ProcessedCourse["skills"];
}): IndustryKey {
  if (!title) return "other";

  const texts: string[] = [];

  if (title) texts.push(title);
  if (subjectEn) texts.push(subjectEn);
  if (subjectEs) texts.push(subjectEs);
  if (overview) texts.push(stripHtml(overview));

  if (subjects) {
    for (const s of subjects) {
      if (s.name) texts.push(s.name);
    }
  }

  if (skills) {
    for (const skill of skills) {
      if (skill.category) texts.push(skill.category);
      if (skill.subcategory) texts.push(skill.subcategory);
    }
  }

  if (!texts.length) return "other";

  const corpus = texts
    .join(" | ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, ""); // quitar acentos para comparar

  let bestKey: IndustryKey = "other";
  let bestScore = 0;

  (Object.keys(INDUSTRY_KEYWORDS) as IndustryKey[]).forEach((key) => {
    if (key === "other") return;

    const keywords = INDUSTRY_KEYWORDS[key];
    let score = 0;

    for (const rawKw of keywords) {
      const kw = rawKw
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
      if (!kw) continue;

      if (corpus.includes(kw)) {
        score += 1;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestKey = key;
    }
  });

  // si no hubo ninguna coincidencia, "other"
  return bestScore > 0 ? bestKey : "other";
}

// helper muy simple para quitar HTML en overview
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, " ");
}
